import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

export interface ThemeSnippet {
    id: string
    authorName: string
    text: string
}

interface CustomerSayThemeModalProps {
    label: string
    count: number
    blurb: string
    keywords: string[]
    snippets: ThemeSnippet[]
    loading?: boolean
    onClose: () => void
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Bold any keyword occurrences in the snippet text (case-insensitive).
const highlight = (text: string, keywords: string[]): React.ReactNode => {
    const pattern = keywords.filter(Boolean).map(escapeRegExp).sort((a, b) => b.length - a.length).join('|')
    if (!pattern) return text
    const regex = new RegExp(pattern, 'ig')
    const nodes: React.ReactNode[] = []
    let last = 0
    let m: RegExpExecArray | null
    while ((m = regex.exec(text)) !== null) {
        if (m.index > last) nodes.push(text.slice(last, m.index))
        nodes.push(<strong key={m.index} className="font-headline text-black">{m[0]}</strong>)
        last = m.index + m[0].length
        if (m.index === regex.lastIndex) regex.lastIndex++
    }
    if (last < text.length) nodes.push(text.slice(last))
    return nodes
}

const Snippet: React.FC<{ snippet: ThemeSnippet; keywords: string[] }> = ({ snippet, keywords }) => {
    const [expanded, setExpanded] = useState(false)
    const long = snippet.text.length > 150
    return (
        <div className="rounded-2xl bg-[#F7F6F3] p-4">
            <p className={`font-body text-[15px] leading-[1.55] text-[#2A2A2A] ${!expanded && long ? 'line-clamp-3' : ''}`}>
                “{highlight(snippet.text, keywords)}”
            </p>
            <div className="mt-2 flex items-center gap-2">
                <span className="font-headline text-[12px] text-[#6B6B6B]">{snippet.authorName}</span>
                {long && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="text-[12px] text-orange hover:underline underline-offset-2"
                    >
                        {expanded ? 'Show less' : 'Read more'}
                    </button>
                )}
            </div>
        </div>
    )
}

// Amazon-style theme panel: a short summary + representative review snippets for the
// selected "Customers say" theme. Rendered via portal (see LIGHTBOX_MODAL_PATTERN.md).
const CustomerSayThemeModal: React.FC<CustomerSayThemeModalProps> = ({
    label,
    count,
    blurb,
    keywords,
    snippets,
    loading = false,
    onClose,
}) => {
    useEffect(() => {
        const originalStyle = window.getComputedStyle(document.body).overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = originalStyle
        }
    }, [])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [onClose])

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose()
    }

    const content = (
        <div
            className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label={`What customers say about ${label}`}
        >
            <div
                className="bg-white rounded-[24px] shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4 p-5 md:p-6 border-b border-[#E9E9E9]">
                    <div className="font-headline text-[18px] md:text-[20px] text-black leading-snug">
                        {count} {count === 1 ? 'customer mentions' : 'customers mention'}{' '}
                        <span className="text-orange">‘{label}’</span>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="text-[#8A8A8A] hover:text-black transition-colors -m-1 p-1 flex-none"
                    >
                        <X size={22} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 md:p-6">
                    <p className="font-body text-[15px] md:text-[16px] leading-[1.6] text-[#1A1A1A] mb-6">{blurb}</p>
                    {loading ? (
                        <div className="py-6 text-[14px] text-[#8A8A8A]">Loading reviews…</div>
                    ) : snippets.length === 0 ? null : (
                        <>
                            <div className="font-headline text-[11px] uppercase tracking-[0.08em] text-[#8A8A8A] mb-3">
                                A few highlights
                            </div>
                            <div className="space-y-3">
                                {snippets.map((s) => (
                                    <Snippet key={s.id} snippet={s} keywords={keywords} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )

    return createPortal(content, document.body)
}

export default CustomerSayThemeModal
